import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams,useLocation,useNavigate } from 'react-router-dom';
import { useEstimations } from '../../context/EstimationContext';
import { useLoading } from '../../context/LoadingContext';
import { formatCurrency } from '../../utils/format';
import Swal from 'sweetalert2';
import SignatureCanvas from 'react-signature-canvas';
import { useRoutePath } from '../../utils/getRoutePath';
import { 
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  CalendarIcon,
  CheckIcon,
  ShoppingBagIcon,
  XMarkIcon,
  InformationCircleIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  UserCircleIcon,
  TicketIcon,
  CurrencyDollarIcon,
  ClockIcon,
  TrophyIcon,
  PhoneIcon,
  EnvelopeIcon,
  HomeIcon,
  TagIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { AxeIcon } from 'lucide-react';

const AcceptEstimation = () => {
  const { id: encodedId } = useParams();
  const { getGuestEstimationById,updateGuestEstimation } = useEstimations();
  const location = useLocation();
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();
  const getRoutePath = useRoutePath();
  const [estimation, setEstimation] = useState(null);
  const [signature, setSignature] = useState('');
  const [signedByName, setSignedByName] = useState('');
  const [signedById, setSignedById] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isRequestChangeOpen, setIsRequestChangeOpen] = useState(false);
  const [isAcceptEstimationOpen, setIsAcceptEstimationOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [acceptedContent, setAcceptedContent] = useState(false);

  // Additional data
  
  const [errors, setErrors] = useState({});
  
  // Refs to prevent API loops
  const hasFetchedEstimation = useRef(false);
  
  const signatureRef = useRef(null);

  // Decode base64 ID
  const decodeId = (encodedId) => {
    try {
      // Replace URL-safe characters
      let paddedId = encodedId.replace(/-/g, '+').replace(/_/g, '/');
      while (paddedId.length % 4) paddedId += '=';
  
      // Decode to binary string
      const binary = atob(paddedId);
  
      // Remove salt if used (first 7 bytes)
      const actualBinary = binary.slice(7);
  
      // Convert binary string to number
      let result = 0;
      for (let i = 0; i < actualBinary.length; i++) {
        result = result * 256 + actualBinary.charCodeAt(i);
      }
  
      return result;
    } catch {
      return null;
    }
  };
  
 
  useEffect(() => {
    
    const decodedId = decodeId(encodedId);
    if(!decodedId){
      getRoutePath(navigate('/'))
    }
    if(decodedId) { 
      if(estimation?.signature){
        setAcceptedContent(true);
       
      }else{
        
        setAcceptedContent(false);
       
      }
    }
        
  }, [location.pathname,encodedId,estimation,getRoutePath,navigate]);

  // Load estimation data
  const fetchEstimationData = useCallback(async () => {
    const decodedId = decodeId(encodedId);
    if (!decodedId || hasFetchedEstimation.current) return;
    
    hasFetchedEstimation.current = true;
    showLoading();
    
    try {
      const response = await getGuestEstimationById(decodedId);
      if (response?.data) {
          setEstimation(response.data);
        // Pre-fill customer name if available
        if (response.data.customer) {
          setSignedByName(response.data.customer.name || '');
          setSignedById(response.data.customer?.id || '');
        }
      }
    } catch (error) {
      console.error('Error fetching estimation data:', error);
      Swal.fire({
        title: 'Error',
        text: 'Estimation not found or access denied.',
        icon: 'error'
      });
    } finally {
      hideLoading();
    }
  }, [encodedId, getGuestEstimationById, showLoading, hideLoading]);

  
  useEffect(() => {
    if (encodedId) {
      fetchEstimationData();
      
    }
  }, [encodedId, fetchEstimationData ]);

  const handleSignatureChange = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setSignature(signatureData);
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignature('');
    }
  };

  const handleAcceptAndRequestChangeEstimation = async (type) => {
    showLoading()
    setErrors({});
    if(type === 'accept'){
        if (!signature || signature === '') {
            Swal.fire({
                title: 'Signature Required',
                text: 'Please provide your digital signature to accept this estimation.',
                icon: 'warning'
            });
            setErrors({...errors, signature: 'Signature is required'});
            return;
        }

        if (!signedByName.trim()) {
            Swal.fire({
                title: 'Name Required',
                text: 'Please provide your name to accept this estimation.',
                icon: 'warning'
            });
            setErrors({...errors, signedByName: 'Name is required'});
            return;
        }
    }else if(type === 'request_change'){
        if (!message || message === '') {
            setErrors({...errors, message: 'Message is required'});
            return;
        }
    }
    setIsSubmitting(true);
    
    try {
      const decodedId = decodeId(encodedId);
      const data = {
        id: decodedId,
        signature: signature,
        signed_by_name: signedByName,
        signed_by_id: signedById,
        accepted_at: new Date().toISOString(),
        request_change_message:message,
        type: type,
        status:3
      };
      const response = await updateGuestEstimation(decodedId,data);
      
      if (response?.status === 200) {
       
        if(type === 'accept'){
          estimation.signature = signature
          setAcceptedContent(true);
        }else{
          Swal.fire({
            title: 'Success!',
            text: response?.message,
            icon: 'success'
          }).then(() => {
            // Redirect to a thank you page or close the window
            setIsAcceptEstimationOpen(false);
            setIsRequestChangeOpen(false);
          });
        }
        hideLoading()
      }else if(response?.status === 409 ){
         
        Swal.fire({
          title: 'Info!',
          text: response?.error?.message,
          icon: 'info'
        }).then(() => {
          // Redirect to a thank you page or close the window
          setIsAcceptEstimationOpen(false);
          setIsRequestChangeOpen(false);
        });
        hideLoading()
      } else {
        hideLoading()
        throw new Error('Failed to accept estimation');
      }
    } catch (error) {
      console.error('Error accepting estimation:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to accept estimation. Please try again.',
        icon: 'error'
      });
      hideLoading()
    } finally {
      hideLoading()
      setIsSubmitting(false);
    }
  };

  const getTotals = () => {
    if (!estimation) return {};
    
    return {
      productsTotal: parseFloat(estimation.total_products_services_tax_fee) || 0,
      driverLaborTotal: parseFloat(estimation.total_driver_labor_time_cost) || 0,
      expensesTotal: parseFloat(estimation.total_billable_expenses_cost) || 0,
      jobTotal: parseFloat(estimation.job_total) || 0,
      paymentsTotal: parseFloat(estimation.total_payments_deposits_cost) || 0,
      totalDue: parseFloat(estimation.total_due) || 0,
      jobCost: parseFloat(estimation.job_cost) || 0,
      grossProfit: parseFloat(estimation.total_gross_profit_cost) || 0,
      grossProfitPercentage: parseFloat(estimation.total_gross_profit_percentage) || 0
    };
  };

  if (!estimation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading Estimation...</h2>
          <p className="text-gray-600">Please wait while we load the estimation details.</p>
        </div>
      </div>
    );
  }

  const totals = getTotals();

  return acceptedContent ? (
   
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#009efb] to-[#0089db] px-3">
        <div className="bg-white rounded-xl shadow-lg p-10 max-w-lg w-full text-center border border-blue-100">
          <div className="flex justify-center mb-4">
            <CheckIcon className="h-16 w-16 text-green-500 bg-green-100 rounded-full p-3 shadow" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Thank You!</h1>
          <p className="text-lg text-gray-700 mb-4">
            Your estimation has been <span className="font-semibold text-green-600">accepted successfully</span>.
          </p>
          <div className="flex justify-center mb-4">
            <img
              src={estimation?.company?.logo?.path}
              alt={estimation?.company?.name ? `${estimation.company.name} logo` : "Azip Logo"}
              className="h-16 w-auto mx-auto"
              style={{ maxHeight: 64 }}
            />
          </div>
          <p className="text-gray-600 mb-6">
            Thank you for choosing <span className="font-semibold text-[#009efb]">{estimation?.company?.name}</span>.<br />
            We appreciate your trust in our services.
          </p>
          <div className="text-sm text-gray-400">
            If you have any questions, please contact us at <span className="underline">{estimation?.company?.primary_email || `support@${estimation?.company?.alias?.toLowerCase()}.com`}
            </span>
          </div>
        </div>
      </div>
    ) : (
        <div className="min-h-screen">
          <div className="max-w-10xl mx-auto px-4 py-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-4 rounded-t-lg">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between p-4 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3 md:mb-0">
                    <DocumentTextIcon className="h-8 w-8 text-white" />
                    <div>
                      <h1 className="text-xl sm:text-2xl font-bold text-white">Accept Estimation</h1>
                      <p className="text-sm sm:text-base text-blue-100">Review and accept this estimation</p>
                    </div>
                  </div>
                  <div className="text-left md:text-right text-white">
                    <div className="text-xs sm:text-sm opacity-90">Estimation #</div>
                    <div className="text-lg sm:text-xl font-bold">{estimation.estimation_number || 'N/A'}</div>
                  </div>
                </div>

              </div>
            </div>

            {/* Company*/}
            <div className="grid grid-cols-1 xl:grid-cols-1 gap-6 mb-8">
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                  <div className="flex items-center">
                      <BuildingOfficeIcon className="text-[#009efb] h-5 w-5  mr-3" />
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center">Company</h2>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="space-y-2 text-center">
                      {estimation?.company?.logo?.path && (
                          <img 
                              src={estimation?.company?.logo?.path} 
                              width="300" 
                              alt={`${estimation?.company?.name || "Company"} logo`} 
                              className="mx-auto mb-3"
                          />
                      )}
                      
                      <p className="text-gray-600 flex items-center justify-center">
                        <HomeIcon className="h-4 w-4 mr-2 text-gray-500" />
                        {estimation?.company?.address1}
                      </p>

                      <p className="text-gray-600 flex items-center justify-center">
                        <MapPinIcon className="h-4 w-4 mr-2 text-gray-500" />
                        {estimation?.company?.city}, {estimation?.company?.state} {estimation?.company?.zipcode}
                      </p>

                      <p className="text-gray-600 flex items-center justify-center">
                        <PhoneIcon className="h-4 w-4 mr-2 text-gray-500" />
                        {estimation?.company?.primary_number}
                      </p>

                      <p className="text-gray-600 flex items-center justify-center">
                        <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-500" />
                        {estimation?.company?.primary_email}
                      </p>
    
                    </div>
                  
                  </div>
                </div>
              </div>
            </div>

            {/* Estimation Details - Similar to EstimationView but simplified */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">

            {/* Customer Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                    Customer
                  </h3>
                </div>

                <div className="p-6 space-y-3">
                  {/* Name */}
                  <div className="flex justify-between items-center">
                    <span className="flex items-center text-sm text-gray-600 font-medium">
                      <IdentificationIcon className="h-4 w-4 mr-1 text-[#009efb]" /> Name
                    </span>
                    <span className="text-gray-900">
                      {estimation.customer?.customerName || estimation.customer?.name || 'N/A'}
                    </span>
                  </div>

                  {/* Contact */}
                  <div className="flex justify-between items-center">
                    <span className="flex items-center text-sm text-gray-600 font-medium">
                      <UserCircleIcon className="h-4 w-4 mr-1 text-[#009efb]" /> Contact
                    </span>
                    <span className="text-gray-900">
                      {estimation.primary_contact?.first_name} {estimation.primary_contact?.last_name}
                    </span>
                  </div>

                  {/* Service Locations */}
                  <div>
                    <span className="flex items-center text-sm text-gray-600 font-medium mb-2">
                      <MapPinIcon className="h-4 w-4 mr-1 text-[#009efb]" /> Service Locations
                    </span>

                    {estimation.service_locations && estimation.service_locations.length > 0 ? (
                      <div className="space-y-2">
                        {estimation.service_locations.map((location, index) => (
                          <div
                            key={index}
                            className="border border-gray-100 rounded-md p-3 bg-gray-50"
                          >
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Nickname</span>
                              <span className="text-gray-800 font-medium">
                                {location.location_nickname || 'Primary Location'}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Street Address</span>
                              <span className="text-gray-800">{location.street_address}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">City</span>
                              <span className="text-gray-800">
                                {location.city}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">State</span>
                              <span className="text-gray-800">
                                {location.state}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Zipcode</span>
                              <span className="text-gray-800">
                                {location.zipcode}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500 ml-6">No location specified</div>
                    )}
                  </div>
                </div>
              </div>

      
            {/* Estimation Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                  Estimation Details
                </h3>
              </div>

              <div className="p-6 space-y-3">

                {/* Number */}
                <div className="flex justify-between items-center">
                  <span className="flex items-center text-sm text-gray-600 font-medium">
                    <IdentificationIcon className="h-4 w-4 mr-1 text-[#009efb]" /> Estimation Number
                  </span>
                  <span className="text-gray-900">
                    {estimation.estimation_number || 'N/A'}
                  </span>
                </div>

                {/* Date */}
                <div className="flex justify-between items-center">
                  <span className="flex items-center text-sm text-gray-600 font-medium">
                    <CalendarIcon className="h-4 w-4 mr-1 text-[#009efb]" /> Date
                  </span>
                  <span className="text-gray-900">
                    {estimation.requested_on || 'N/A'}
                  </span>
                </div>

                {/* PO# */}
                <div className="flex justify-between items-center">
                  <span className="flex items-center text-sm text-gray-600 font-medium">
                    <TicketIcon className="h-4 w-4 mr-1 text-[#009efb]" /> PO#
                  </span>
                  <span className="text-gray-900">
                    {estimation.po_number || 'N/A'}
                  </span>
                </div>
    

                {/* Total */}
                <div className="flex justify-between items-center">
                  <span className="flex items-center text-sm text-gray-600 font-medium">
                    <CurrencyDollarIcon className="h-4 w-4 mr-1 text-[#009efb]" /> Total
                  </span>
                  <span className="text-lg font-bold text-[#009efb]">
                    {formatCurrency(totals.jobTotal)}
                  </span>
                </div>
              </div>
            </div>
    
          </div>

            {/* Estimation Items - Compact View */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <TagIcon className="h-5 w-5 mr-2 text-[#009efb]" />
                  Estimation Items
                </h3>
              </div>
              <div className="p-6">
                {/* Products & Services */}
                {(estimation.products || []).length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <ShoppingBagIcon className="h-4 w-4 mr-2 text-[#009efb]" />
                      Products & Services
                    </h4>
                    <div className="space-y-3">
                      {estimation.products.map((item, index) => (
                        <div key={item.id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{item.description}</div>
                            <div className="text-sm text-gray-500">Qty: {item.quantity} × {formatCurrency(item.rate || 0)}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">{formatCurrency(item.total || 0)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Driver & Labor Times */}
                {(estimation.driver_labor_times || []).length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <ClockIcon className="h-4 w-4 mr-2 text-[#009efb]" />
                      Driver & Labor Times
                    </h4>
                    <div className="space-y-3">
                      {estimation.driver_labor_times.map((item, index) => (
                        <div key={item.id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {item?.tech?.first_name || item?.tech?.last_name
                                ? `${item?.tech?.first_name || ''} ${item?.tech?.last_name || ''}`
                                : 'Unknown'} - {item?.formated_date || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              Drive: {item.driverHours}h {item.driverMinutes}m | Labor: {item.laborHours}h {item.laborMinutes}m
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">{formatCurrency(item.total || 0)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expenses */}
                {(estimation.expenses || []).length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <CurrencyDollarIcon className="h-4 w-4 mr-2 text-[#009efb]" />
                      Expenses
                    </h4>
                    <div className="space-y-3">
                      {estimation.expenses.map((item, index) => (
                        <div key={item.id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{item.purchased_from || 'Unknown Vendor'}</div>
                            <div className="text-sm text-gray-500">{item.date} - {item?.category?.name || 'N/A'}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">{formatCurrency(item.amount || 0)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Totals Summary */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <CurrencyDollarIcon className="h-4 w-4 mr-2 text-[#009efb]" />
                    Totals Summary
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="flex items-center text-gray-600">
                        <ShoppingBagIcon className="h-3 w-3 mr-1" /> Products Total:
                      </span>
                      <span className="font-semibold">{formatCurrency(totals.productsTotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="flex items-center text-gray-600">
                        <ClockIcon className="h-3 w-3 mr-1" /> Driver/Labor Total:
                      </span>
                      <span className="font-semibold">{formatCurrency(totals.driverLaborTotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="flex items-center text-gray-600">
                        <CurrencyDollarIcon className="h-3 w-3 mr-1" /> Expenses Total:
                      </span>
                      <span className="font-semibold">{formatCurrency(totals.expensesTotal)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span className="flex items-center">
                        <TrophyIcon className="h-4 w-4 mr-1" /> Total Due:
                      </span>
                      <span className="text-[#009efb]">{formatCurrency(totals.totalDue)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Digital Signature Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 rounded-t-lg">
                <div className="flex items-center">
                  <CheckIcon className="h-6 w-6 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">Digital Signature</h2>
                </div>
              </div>
              
              <div className="p-6">
                
                  <div className="space-y-6">
                    {/* Customer Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <UserIcon className="h-4 w-4 mr-2 text-gray-500" />
                      Signed By <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={signedByName}
                      onChange={(e) => setSignedByName(e.target.value)}
                      className={`${errors?.signedByName ? 'border-red-500' : 'border-gray-300'} w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500`}
                      placeholder="Enter your full name"
                      required
                      readOnly
                      disabled
                    />
                    {errors?.signedByName && (
                        <p className="mt-1 text-sm text-red-600">{errors?.signedByName}</p>
                    )}
                  </div>

                    {/* Digital Signature */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <DocumentTextIcon className="h-4 w-4 mr-2 text-gray-500" />
                        Digital Signature <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="border border-gray-300 rounded-lg p-4 bg-white">
                        <div className="w-full max-w-full overflow-hidden">
                          <SignatureCanvas
                            ref={signatureRef}
                            penColor='green'
                            canvasProps={{
                              className: 'signature-canvas border border-gray-200 rounded w-full max-w-full',
                              style: { width: "100%", height: 200 }
                            }}
                            onEnd={handleSignatureChange}
                          />
                        </div>
                        <div className="flex justify-end mt-3">
                          <span
                            onClick={clearSignature}
                            className="text-red-500 hover:underline cursor-pointer text-sm"
                          >
                            Clear Signature
                          </span>
                        </div>

                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        Please sign above to provide your digital signature.
                      </p>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-2 text-gray-500" />
                        Terms and Conditions <span className="text-red-500 ml-1">*</span>
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        By accepting this estimation, you agree to the terms and conditions outlined in this document. 
                        This digital signature has the same legal effect as a handwritten signature.
                      </p>
                      <div className="flex items-start space-x-3">
                        <div className="flex items-center h-5">
                          <input
                            checked={termsAccepted}
                            onChange={() => setTermsAccepted(!termsAccepted)}
                            type="checkbox"
                            id="terms"
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            required
                          />
                        </div>
                        <label htmlFor="terms" className="text-sm text-gray-700">
                          I agree to the terms and conditions and confirm that I have the authority to accept this estimation.
                        </label>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      
                      <button
                        onClick={() => setIsAcceptEstimationOpen(true)}
                        disabled={isSubmitting || !signature || !signedByName.trim() || !termsAccepted}
                        className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Accepting...
                          </>
                        ) : (
                          <>
                            <CheckIcon className="h-5 w-5 mr-2" />
                            Accept Estimation
                          </>
                        )}
                      </button>
                      <button onClick={() => setIsRequestChangeOpen(true)} className='flex-1 bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center'>
                          <AxeIcon className='h-5 w-5 mr-2' />
                          Request a Change
                      </button>

                      {/* Accept Estimation Modal */}
                      {isAcceptEstimationOpen && (
                          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                          <div className="relative w-full max-w-2xl mx-4 rounded-2xl bg-white p-6 shadow-2xl">
                            
                            {/* Header */}
                            <div className="flex items-center justify-between border-b pb-3">
                              <h2 className="text-xl font-semibold text-gray-800">
                                Accept Estimation
                              </h2>
                              <button
                                onClick={() => setIsAcceptEstimationOpen(false)}
                                className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                    
                            {/* Body */}
                            <div className="mt-4 space-y-4">
                              <div className="flex items-start gap-2 mb-4 rounded-lg bg-blue-50 border border-blue-200 p-4">
                                  <InformationCircleIcon className="h-12 w-12 sm:h-10 sm:w-10 md:h-10 md:w-109 lg:h-10 lg:w-10 text-blue-600 mt-0.5" />
                                  <p className="text-sm sm:text-base text-blue-700">
                                      Please take a moment to review the estimation carefully before confirming. Once confirmed, we will proceed based on your approval.
                                  </p>
                              </div>
          
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                Signed By
                                </label>
                                <div className="rounded-lg bg-gray-50 p-3 text-gray-900">
                                  <span className="text-gray-900">{signedByName}</span>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                Signature
                                </label>
                                <div className="rounded-lg bg-gray-50 p-3 text-gray-900">
                                  <img src={signature} alt="Signature" />
                                  
                                </div>
                              </div>

                            </div>
                    
                            {/* Footer */}
                            <div className="mt-6 flex justify-end space-x-3">
                              <button
                                onClick={() => setIsAcceptEstimationOpen(false)}
                                className="flex items-center rounded-lg bg-gray-600 px-6 py-3 text-white font-medium shadow hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition"
                              >
                                <XMarkIcon className="mr-2 h-4 w-4" />
                                Cancel
                              </button>
                              {!isSubmitting ? 
                                <button
                                  onClick={() => handleAcceptAndRequestChangeEstimation('accept')}
                                  className="flex items-center rounded-lg bg-green-600 px-6 py-2.5 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition"
                                >
                                  <CheckIcon className="mr-2 h-4 w-4" />
                                  Confirm
                                </button>
                              :
                                <button className="flex items-center rounded-lg bg-green-600 px-6 py-2.5 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition disabled:cursor-not-allowed" disabled>
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                  Loading...
                                </button>
                              }
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Request Change Modal */}
                      {isRequestChangeOpen && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 relative">
                              
                              {/* Header */}
                              <div className="flex items-center justify-between border-b pb-3">
                                  <h2 className="text-xl font-semibold text-gray-800">
                                  Request a Change
                                  </h2>
                                      <button
                                  onClick={() => setIsRequestChangeOpen(false)}
                                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition"
                                      >
                                  <XMarkIcon className="h-5 w-5" />
                                  </button>
                              </div>

                              {/* Body */} 
                              <div className="mt-4 space-y-4">
                                  <div className="flex items-start gap-2 mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
                                      <InformationCircleIcon className="h-10 w-10 sm:h-10 sm:w-10 md:h-10 md:w-109 lg:h-10 lg:w-10 text-blue-600 mt-0.5" />
                                      <p className="text-sm sm:text-base leading-relaxed text-blue-700">
                                          Please provide a clear and detailed description of the changes you would like to request.
                                      </p>
                                  </div>
                                  <div>
                                      <textarea
                                          name="message"
                                          rows="6"
                                          className={`${errors?.message ? 'border-red-500' : 'border-gray-300'} w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 resize-none`}
                                          placeholder="Type your request here..."
                                          value={message}
                                          onChange={(e) => {
                                              const val = e.target.value;
                                              setMessage(val);
                                          
                                              if (val.trim() === "") {
                                                setErrors({ ...errors, message: "Message is required" });
                                              } else {
                                                setErrors({ ...errors, message: "" });
                                              }
                                          }}
                                      ></textarea>
                                      <div className="space-y-0">
                                          {errors?.message && (
                                              <p className="text-sm text-red-600">{errors?.message}</p>
                                          )}
                                      </div>
                                      
                                  </div>
                              </div>

                              {/* Footer */}
                              <div className="flex justify-end space-x-3 mt-2">
                                  <button
                                  onClick={() => setIsRequestChangeOpen(false)}
                                  className="flex items-center rounded-lg bg-gray-600 px-6 py-3 text-white font-medium shadow hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition"
                                  >
                                      <XMarkIcon className='h-4 w-4 mr-2' />
                                      Cancel
                                  </button>
                                  {!isSubmitting ? 
                                    <button  onClick={() => handleAcceptAndRequestChangeEstimation('request_change')} className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
                                
                                    <CheckIcon className='h-4 w-4 mr-2' />
                                    Submit
                                    </button>
                                  :
                                    <button className="flex items-center rounded-lg bg-green-600 px-6 py-3 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition disabled:cursor-not-allowed" disabled>
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Loading...
                                    </button>
                                  }
                              </div>
                          </div>
                      </div>
                      )}

                    </div>
                  </div>
                
              </div>
            </div>
          </div>
        </div>
      );
};

export default AcceptEstimation;